import React, { useCallback, useMemo } from 'react';
import { Button, TableCell, TableRow } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import { Box, Stack } from '@mui/material';
import { formatNumber } from 'c-lib';
import type { PopDisplays_VirtualCampaignSummary } from 'c-sdk';

type Props = {
    summary: PopDisplays_VirtualCampaignSummary; // new shape
    onSummaryOpen: (rowId: number, displayId: number) => void;
};

const VirtualCampaignSummaryRow: React.FC<Props> = ({ summary, onSummaryOpen }) => {
    const t = useCommonTranslation();

    const channelFrameId = summary.channel_frame_id;
    const channel = summary.channelFrame?.channel ?? '';
    const frame = summary.channelFrame?.frame ?? '';
    const source = summary.channelFrame?.source ?? '';

    const count = summary.count ?? 0;
    const seconds = summary.duration ?? 0;
    const time = summary.time ?? '';

    const minDate = summary.min_date ?? '';
    const maxDate = summary.max_date ?? '';

    type RowVM = {
        key: React.Key;
        rowId: number;
        address: string;
        frame_id: string | number | '';
        slots: number | string | '';
        slot_duration: number | string | '';
        owner: string;
        displayOpenId: number;
    };

    const displayRows: RowVM[] = useMemo(() => {
        const displays = summary.channelFrame?.displays;

        if (Array.isArray(displays) && displays.length > 0) {
            return displays.map(d => ({
                key: `${channelFrameId}-${d.id ?? 'display'}`,
                rowId: channelFrameId,
                address: d.address ?? d.name ?? '',
                frame_id: d.frame_id ?? '',
                slots: d.slots ?? '',
                slot_duration: d.slot_duration ?? '',
                owner: d.owner ?? '',
                displayOpenId: d.id ?? channelFrameId,
            }));
        }

        return [
            {
                key: `${channelFrameId}-fallback`,
                rowId: channelFrameId,
                address: '',
                frame_id: '',
                slots: '',
                slot_duration: '',
                owner: '',
                displayOpenId: channelFrameId,
            },
        ];
    }, [summary.channelFrame?.displays, channelFrameId]);

    const handleOpen = useCallback(
        (rowId: number, displayId: number) => {
            onSummaryOpen(rowId, displayId);
        },
        [onSummaryOpen],
    );

    return (
        <>
            {displayRows.map(row => (
                <TableRow key={row.key}>
                    <TableCell>{row.address}</TableCell>

                    <TableCell>{channel}</TableCell>
                    <TableCell>{frame}</TableCell>
                    <TableCell>{row.frame_id}</TableCell>

                    <TableCell>{row.slots}</TableCell>
                    <TableCell>{row.slot_duration}</TableCell>

                    <TableCell>{row.owner}</TableCell>
                    <TableCell>{source}</TableCell>

                    <TableCell>
                        <Stack>
                            <Box>
                                {t(
                                    'Modules.PopDisplays.VirtualCampaignBuilder.summary.table.count',
                                    {
                                        count: formatNumber(count),
                                    },
                                )}
                            </Box>
                            <Box>
                                {t(
                                    'Modules.PopDisplays.VirtualCampaignBuilder.summary.table.secs',
                                    {
                                        count: formatNumber(seconds),
                                    },
                                )}
                            </Box>
                            <Box>
                                {t(
                                    'Modules.PopDisplays.VirtualCampaignBuilder.summary.table.time',
                                    {
                                        count: time,
                                    },
                                )}
                            </Box>
                        </Stack>
                    </TableCell>

                    <TableCell>{minDate}</TableCell>
                    <TableCell>{maxDate}</TableCell>

                    <TableCell>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpen(row.rowId, row.displayOpenId)}
                        >
                            {t(
                                'Modules.PopDisplays.VirtualCampaignBuilder.summary.table.playbackSummaryLabel',
                            )}
                        </Button>
                    </TableCell>
                </TableRow>
            ))}
        </>
    );
};

export default VirtualCampaignSummaryRow;
